import { Snackbar, Alert } from "@mui/material"; // Import MUI components
import React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSession } from "0.Mine/2.Context/sessionContext";
import axiosInstance from "0.Mine/axiosInstance";

export enum QlickupActions {
  GET = "get",
  POST = "post",
}

export interface AdditionalParams {
  [key: string]: string | number | boolean | null | Date | string[] | File | File[];
}

export const useQlickupAPI = () => {
  const navigate = useNavigate();
  const { session, clearSession } = useSession();

  useEffect(() => {
    if (!session) {
      navigate("/signin", { replace: true });
    }
    if (!session?.user) {
      navigate("/signin", { replace: true });
    }
  });

  /**
   * Makes an API call to the Qlickup backend
   * @param businessid - The business ID
   * @param action - The action to perform
   * @param data - The primary data payload
   * @param requestString - The request endpoint
   * @param apiAction - GET or POST method
   * @param additionalParams - Optional key-value pairs to include in the request
   * @returns The response data or null if there was an error
   */
  const QlickUpAPI = async (
    businessid: string,
    action: string,
    data: any,
    requestString: string,
    apiAction: QlickupActions = QlickupActions.GET,
    additionalParams?: AdditionalParams
  ) => {
    const userid = session.user.id;
    const token = session.user.token;

    const payloadForQuery: Record<string, string> = {
      userid: userid,
      token: token ?? "",
      action: action,
      businessid: businessid,
      data: JSON.stringify(data),
    };

    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        payloadForQuery[key] = String(value);
      });
    }

    try {
      const queryString = new URLSearchParams(payloadForQuery).toString();
      const endpoint = `${apiAction === QlickupActions.POST ? "POST" : "GET"}/${requestString}.php`;

      const response =
        apiAction === QlickupActions.POST
          ? await axiosInstance.post(`${endpoint}?${queryString}`)
          : await axiosInstance.get(`${endpoint}?${queryString}`);

      return response.data;
    } catch (error: any) {
      console.log(error);

      if (error.status && error.status === 401) {
        // Delay redirect to allow the snackbar to be seen
        setTimeout(() => {
          navigate("/signin", { replace: true });
        }, 2000);
        clearSession();

        return null;
      }
      return null;
    }
  };

  /**
   * Makes an API call to the Qlickup backend using FormData
   * @param businessid - The business ID
   * @param action - The action to perform
   * @param data - The primary data payload
   * @param requestString - The request endpoint
   * @param apiAction - Only POST is supported for FormData
   * @param formData - FormData object containing all parameters including files
   * @returns The response data or null if there was an error
   */
  const QlickUpAPIWithFormData = async (
    businessid: string,
    action: string,
    data: any,
    requestString: string,
    apiAction: QlickupActions = QlickupActions.POST,
    formData: FormData
  ) => {
    if (apiAction !== QlickupActions.POST) {
      console.error("FormData can only be used with POST requests");
      return null;
    }

    const userid = session.user.id;
    const token = session.user.token;

    // Add authentication and core parameters
    formData.append("userid", userid);
    formData.append("token", token ?? "");
    formData.append("action", action);
    formData.append("businessid", businessid);

    // Add data if provided
    if (data) {
      formData.append("data", JSON.stringify(data));
    }

    try {
      const endpoint = `POST/${requestString}.php`;

      // Use axios to post the FormData
      const response = await axiosInstance.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error: any) {
      console.error("API Error:", error);

      if (error.status && error.status === 401) {
        // Delay redirect to allow the snackbar to be seen
        setTimeout(() => {
          navigate("/signin", { replace: true });
        }, 2000);
        clearSession();
      }
      return null;
    }
  };

  return { QlickUpAPI, QlickUpAPIWithFormData };
};
